import { useState, useMemo } from 'react'
import { useMetaData } from '../queries/useMetaData.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import {
  useSaveExecSummary,
  useGenerateExecSummary,
} from '../mutations/useExecSummary.js'
import {
  useSaveCompanyUpdate,
  useGenerateCompanyUpdate,
} from '../mutations/useCompanyUpdate.js'
import {
  useSaveQuarterlyPreread,
  useGenerateQuarterlyPreread,
} from '../mutations/useQuarterlyPreread.js'
import { useAiPreferenceOp } from '../mutations/useAiPreferences.js'
import { useUIStore } from '../store/uiStore.js'
import { timeAgo } from '../data/metaKeys.js'
import { TEAMS } from '../data/teams.js'
import { Q2_START, Q2_END } from '../data/pillars.js'
import RichText from './RichText.jsx'
import FollowUpsSection from './FollowUpsSection.jsx'
import SummaryBulletList from './SummaryBulletList.jsx'
import AutoSizingTextarea from './AutoSizingTextarea.jsx'
import StalenessBar from './StalenessBar.jsx'
import CompanyUpdateView from './CompanyUpdateView.jsx'
import QuarterlyPrereadView, { prereadToMarkdown } from './QuarterlyPrereadView.jsx'
import { resolveStatus } from './StatusPicker.jsx'
import { captureGenerationSnapshot } from '../data/summarySnapshot.js'
import { useRevenueSyncStore } from '../store/revenueSyncStore.js'
import { useIsReadOnly } from '../store/archiveViewStore.js'

/**
 * ExecutiveSummaryCard — top-of-dashboard AI-generated summary.
 *
 * Layout model (2026-04-23 redesign):
 *
 *   1. Header: ✨ · title · generated-when-by · past-weeks · collapse
 *   2. StalenessBar (if exec exists AND any input is newer than
 *      exec.generatedAt) — amber, collapsible
 *   3. Body:
 *        - TLDR section (prose, auto-sizing textarea in edit)
 *        - Wins section (bulleted list w/ attribution pills; per-bullet
 *          editable rows in edit)
 *        - Flags section (same)
 *        - Legend (what the pill colors mean)
 *   4. Action buttons row (Generate / Save / Regenerate / Clear)
 *   5. Follow-ups (open action items, deterministic)
 *   6. AI preferences (writing-style rules)
 *
 * Edit model: per-section, not whole-card. Clicking "Edit" on one
 * section transforms JUST that section; the other two stay in read
 * view so the user keeps the full picture. Save / Cancel commit or
 * discard the per-section draft. The exec summary is saved as a
 * single atomic payload regardless of which section changed.
 *
 * Data shape (new):
 *   execSummary = {
 *     tldr: string,
 *     winBullets:  [{ text, sourceType, sourceLabel, author }],
 *     flagBullets: [{ text, sourceType, sourceLabel, author }],
 *     winsSummary:  string,   // legacy prose, derived from bullets
 *     flagsSummary: string,   // legacy prose, derived from bullets
 *     generatedAt, generatedBy, edited?
 *   }
 *
 * Legacy fallback: if an exec summary has no winBullets/flagBullets
 * (pre-redesign entry), those sections render as prose via RichText.
 * Editing them still uses a textarea (no bullet structure yet) until
 * the next Regenerate produces the bullet format.
 */
export default function ExecutiveSummaryCard() {
  const { data: meta } = useMetaData()
  const { data: goalsData } = useGoalsData()
  const currentUser = useUIStore((s) => s.currentUser) || 'Unknown'
  const readOnly = useIsReadOnly()

  const [expanded, setExpanded] = useState(true)
  const [editingPrefs, setEditingPrefs] = useState(false)
  const [newPrefText, setNewPrefText] = useState('')

  // Per-section drafts — null means "not editing this section".
  const [draftTldr, setDraftTldr] = useState(null)
  const [draftWinBullets, setDraftWinBullets] = useState(null)
  const [draftFlagBullets, setDraftFlagBullets] = useState(null)
  const [draftWinsProse, setDraftWinsProse] = useState(null)
  const [draftFlagsProse, setDraftFlagsProse] = useState(null)

  // Pending preview: fresh AI output that isn't saved yet.
  const [pendingPreview, setPendingPreview] = useState(null)

  // Company Update local state — independent from the exec summary
  // edit flow above. `draft` is the markdown being edited; `preview`
  // is a fresh generation not yet saved; `generating` covers the
  // network round-trip; `copied` flashes the Copy button label.
  const [companyDraft, setCompanyDraft] = useState(null)
  const [companyPreview, setCompanyPreview] = useState(null)
  const [companyGenerating, setCompanyGenerating] = useState(false)
  const [companyCopied, setCompanyCopied] = useState(false)
  const [companyError, setCompanyError] = useState(null)
  // Set of goalKeys that Zoe chose to include from the orphan picker.
  // Zoe checks/unchecks them between generations; the next Generate or
  // Regenerate sends the selected goals' weekly summaries to the AI.
  // Resets when she explicitly clears or after a save (next week
  // starts fresh).
  const [companyOrphanKeys, setCompanyOrphanKeys] = useState(() => new Set())

  // Quarterly Pre-read local state — separate from the Company Update
  // flow above. Same shape: preview holds a fresh generation, draft
  // holds a markdown edit (for inline tweaks via the structured-edit
  // pane), generating covers the round-trip, copied flashes the copy
  // button label, error captures any generation failures.
  const [prereadPreview, setPrereadPreview] = useState(null)
  const [prereadGenerating, setPrereadGenerating] = useState(false)
  const [prereadCopied, setPrereadCopied] = useState(false)
  const [prereadError, setPrereadError] = useState(null)

  const saveExec = useSaveExecSummary()
  const generateExec = useGenerateExecSummary()
  const saveCompany = useSaveCompanyUpdate()
  const generateCompany = useGenerateCompanyUpdate()
  const savePreread = useSaveQuarterlyPreread()
  const generatePreread = useGenerateQuarterlyPreread()
  const prefOp = useAiPreferenceOp()

  // Company Update is leadership-authored. Hide the Generate / Save
  // controls from anyone else — they can still READ a saved update via
  // the section below. Server-side gate is the real boundary; this is
  // just to avoid surfacing buttons that won't work.
  const canAuthorCompanyUpdate =
    currentUser === 'Zoe' || currentUser === 'Ricky'

  const openArchive = useUIStore((s) => s.openArchive)
  const exec = meta.execSummary || null
  const prefs = meta.aiPreferences || []
  const weeklySummariesMap = meta.weeklySummary || {}
  const weeklyHistory = meta.weeklyHistory || {}
  const archiveCount = Object.keys(weeklyHistory).length

  // ── Inputs to send to the AI ──────────────────────────────────────
  // Include `savedBy` so the worker prompt can produce attributed
  // bullets. The worker tags each goal input with `[attribute to: X]`
  // so the AI knows who to credit for each piece of content.
  const weeklySummariesForAi = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const out = []
    for (let pi = 0; pi < goalsData.length; pi++) {
      const pillar = goalsData[pi]
      if (!pillar?.goals) continue
      for (let gi = 0; gi < pillar.goals.length; gi++) {
        const goal = pillar.goals[gi]
        if (!goal || goal.type === 'section') continue
        const key = `p${pi}-g${gi}`
        const value = weeklySummariesMap[key] || {}
        const hasUpdate = Boolean(value.tldr || value.wins || value.flags)
        out.push({
          goalName: `${pillar.pillar} › ${goal.goal || 'Untitled goal'}`,
          dri: goal.dri || null,
          hasUpdate,
          tldr: value.tldr || '',
          wins: value.wins || '',
          flags: value.flags || '',
          savedBy: value.savedBy || '',
        })
      }
    }
    return out
  }, [weeklySummariesMap, goalsData])
  const goalsWithUpdates = weeklySummariesForAi.filter((g) => g.hasUpdate).length
  const totalGoals = weeklySummariesForAi.length

  const openActionItemsForAi = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const today = new Date().toISOString().slice(0, 10)
    const actionItemsMap = meta?.actionItems || {}
    const out = []
    for (const [key, items] of Object.entries(actionItemsMap)) {
      if (!Array.isArray(items)) continue
      const match = /^p(\d+)-g(\d+)$/.exec(key)
      if (!match) continue
      const pi = Number(match[1])
      const gi = Number(match[2])
      const pillar = goalsData[pi]
      const goal = pillar?.goals?.[gi]
      if (!goal) continue
      for (const item of items) {
        if (item.status === 'done') continue
        out.push({
          pillar: pillar.pillar,
          goalName: goal.goal || 'Untitled goal',
          task: item.task,
          owner: item.owner && item.owner !== '—' ? item.owner : null,
          dueDate: item.dueDate || null,
          overdue: Boolean(item.dueDate && item.dueDate < today),
        })
      }
    }
    return out
  }, [meta?.actionItems, goalsData])

  const subgoalUpdatesForAi = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const out = []
    for (const pillar of goalsData) {
      if (!pillar?.goals) continue
      for (const goal of pillar.goals) {
        if (!Array.isArray(goal?.subGoals)) continue
        for (const sub of goal.subGoals) {
          const hasProgress =
            sub.current !== undefined &&
            sub.current !== null &&
            sub.current !== ''
          const hasUpdate = !!(sub.update && String(sub.update).trim())
          if (!hasProgress && !hasUpdate) continue
          out.push({
            pillar: pillar.pillar,
            goalName: goal.name || 'Untitled goal',
            subgoalName: sub.subgoal || 'Untitled sub-goal',
            current: sub.current,
            target: sub.target,
            unit: sub.unit || '',
            update: sub.update || '',
          })
        }
      }
    }
    return out
  }, [goalsData])

  const teamUpdatesForAi = useMemo(() => {
    const map = meta?.teamUpdates || {}
    return TEAMS.map((team) => {
      const entry = map[team.id] || {}
      // Include the team if any of TLDR/Wins/Flags/FYIs has content.
      // The fyis field carries logistics + heads-ups (hiring, OOO,
      // conferences) and feeds the FYIs section of the Company Update.
      if (!entry.tldr && !entry.wins && !entry.flags && !entry.fyis) return null
      return {
        teamName: team.name,
        dri: entry.dri || team.defaultDri || null,
        tldr: entry.tldr || '',
        wins: entry.wins || '',
        flags: entry.flags || '',
        fyis: entry.fyis || '',
        savedBy: entry.savedBy || '',
      }
    }).filter(Boolean)
  }, [meta?.teamUpdates])

  // ── Current display values ────────────────────────────────────────
  // Prefer pendingPreview (AI output not yet saved) → then saved exec.
  const view = pendingPreview || exec || null
  const tldrValue = draftTldr ?? view?.tldr ?? ''
  const winBullets = view?.winBullets || null
  const flagBullets = view?.flagBullets || null
  // Legacy prose used when bullets are missing.
  const winsProse = view?.winsSummary || ''
  const flagsProse = view?.flagsSummary || ''

  const isEditingTldr = draftTldr !== null
  const isEditingWins = draftWinBullets !== null || draftWinsProse !== null
  const isEditingFlags = draftFlagBullets !== null || draftFlagsProse !== null
  const anyEditing = isEditingTldr || isEditingWins || isEditingFlags
  const hasPendingPreview = !!pendingPreview
  const dirty = anyEditing || hasPendingPreview

  // If the saved exec changes while we're in an unrelated draft state,
  // don't blow away the user's in-progress edit. The server only
  // changes exec via user action anyway (regenerate or another
  // teammate's save), so this is defense-in-depth.
  const resetAllDrafts = () => {
    setDraftTldr(null)
    setDraftWinBullets(null)
    setDraftFlagBullets(null)
    setDraftWinsProse(null)
    setDraftFlagsProse(null)
    setPendingPreview(null)
  }

  // ── Actions ───────────────────────────────────────────────────────
  const handleGenerate = async () => {
    // Snapshot the inputs AT THE MOMENT OF GENERATION so the staleness
    // detector can later diff current state against exactly what the
    // AI saw. Reaching into the revenueSyncStore with getState rather
    // than subscribing — we only need the value once, at generation.
    const hsReceipt = useRevenueSyncStore.getState().lastReceipt
    const generatedInputs = captureGenerationSnapshot(meta, goalsData, hsReceipt)

    try {
      const result = await generateExec.mutateAsync({
        weeklySummaries: weeklySummariesForAi,
        subgoalUpdates: subgoalUpdatesForAi,
        openActionItems: openActionItemsForAi,
        aiPreferences: prefs,
        teamUpdates: teamUpdatesForAi,
      })
      // Fresh preview; not saved yet. The snapshot rides along in
      // pendingPreview so if the user edits for a while before saving,
      // we still have the original "what the AI saw" state.
      setPendingPreview({
        tldr: result.tldr || '',
        winBullets: result.winBullets || [],
        flagBullets: result.flagBullets || [],
        winsSummary: result.winsSummary || '',
        flagsSummary: result.flagsSummary || '',
        generatedInputs,
      })
      // Clear any in-flight drafts — the preview replaces them.
      setDraftTldr(null)
      setDraftWinBullets(null)
      setDraftFlagBullets(null)
      setDraftWinsProse(null)
      setDraftFlagsProse(null)
    } catch (err) {
      console.error('[exec-summary] generate failed', err)
      alert("Couldn't generate summary — check the worker logs.")
    }
  }

  const handleSave = () => {
    // Merge: start from the current view (preview or saved), apply any
    // draft overrides, compute derived prose, fire save mutation.
    const base = view || {}
    const nextTldr = draftTldr ?? base.tldr ?? ''

    let nextWinBullets = base.winBullets || null
    let nextWinsSummary = base.winsSummary || ''
    if (draftWinBullets !== null) {
      nextWinBullets = draftWinBullets.filter((b) => b.text.trim())
      nextWinsSummary = nextWinBullets.map((b) => b.text).join(' ')
    } else if (draftWinsProse !== null) {
      // Legacy prose edit — clear bullets, store prose
      nextWinBullets = null
      nextWinsSummary = draftWinsProse
    }

    let nextFlagBullets = base.flagBullets || null
    let nextFlagsSummary = base.flagsSummary || ''
    if (draftFlagBullets !== null) {
      nextFlagBullets = draftFlagBullets.filter((b) => b.text.trim())
      nextFlagsSummary = nextFlagBullets.map((b) => b.text).join(' ')
    } else if (draftFlagsProse !== null) {
      nextFlagBullets = null
      nextFlagsSummary = draftFlagsProse
    }

    saveExec.mutate({
      tldr: nextTldr,
      winBullets: nextWinBullets || [],
      flagBullets: nextFlagBullets || [],
      winsSummary: nextWinsSummary,
      flagsSummary: nextFlagsSummary,
      generatedAt: hasPendingPreview
        ? new Date().toISOString()
        : base.generatedAt,
      generatedBy: hasPendingPreview ? currentUser : base.generatedBy,
      edited: !hasPendingPreview,
      // Preserve the input snapshot from whichever source has one:
      // fresh preview → captured at handleGenerate time
      // existing saved summary being edited → keep the existing snapshot
      //   (editing doesn't change what the AI originally saw)
      generatedInputs: base.generatedInputs || null,
    })
    resetAllDrafts()
  }

  const handleCancel = () => {
    resetAllDrafts()
  }

  const handleClear = () => {
    if (!confirm('Clear the current executive summary?')) return
    saveExec.mutate({ clear: true })
    resetAllDrafts()
  }

  // ── Company Update ──────────────────────────────────────────────────
  const company = meta?.companyUpdate || null

  // Big Picture metrics — pulled from the Revenue pillar's top-level
  // goal if present, plus today's quarter-pacing math. The AI uses
  // these to write the "The Big Picture" section without inventing
  // numbers. Values that aren't derivable get omitted (the AI's prompt
  // tells it not to fabricate).
  const bigPicture = useMemo(() => {
    const out = {
      weekDateLabel: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    }
    // Quarter pacing — bounded 0..100 so a date outside Q2 doesn't yield
    // negative or >100 weirdness in the AI's output.
    const now = Date.now()
    const start = Q2_START.getTime()
    const end = Q2_END.getTime()
    if (end > start) {
      const pct = Math.round(((now - start) / (end - start)) * 100)
      out.quarterPctElapsed = Math.max(0, Math.min(100, pct))
    }
    // Booked revenue — find the Revenue pillar's main revenue goal.
    // The goal's actual name is "Book $XXXk in new revenue" (no "booked"),
    // so match `book`/`booking`/`booked` plus 'revenue', and fall back
    // to the first $-unit non-section goal in the Revenue pillar if no
    // name match. Goals get renamed quarterly; the fallback keeps this
    // resilient without us having to chase the name.
    if (Array.isArray(goalsData)) {
      const revenuePillar = goalsData.find((p) => p?.pillar === 'Revenue')
      if (revenuePillar?.goals) {
        const realGoals = revenuePillar.goals.filter(
          (g) => g && g.type !== 'section'
        )
        const byName = realGoals.find((g) =>
          /book(ed|ing)?\b.*\brevenue/i.test(g?.goal || '')
        )
        const fallback = realGoals.find((g) => g.unit === '$')
        const bookedGoal = byName || fallback || realGoals[0]
        if (bookedGoal) {
          if (typeof bookedGoal.current === 'number') {
            out.bookedRevenueQTD = bookedGoal.current
          }
          if (typeof bookedGoal.target === 'number') {
            out.bookedRevenueTarget = bookedGoal.target
          }
        }
      }
    }
    return out
  }, [goalsData])

  // EO Buyouts pillar content — feeds the Upcoming Transactions
  // section of the Company Update. The pillar's goals + sub-goals
  // already capture the structured client/date data Zoe needs there
  // (transactions to close, current/target, named clients in update
  // text), so we ship the raw pillar slice and let the prompt
  // synthesize a Q1/Q2/Q3 grouping from it.
  const eoBuyoutsContent = useMemo(() => {
    if (!Array.isArray(goalsData)) return null
    const pi = goalsData.findIndex((p) => p?.pillar === 'EO Buyouts')
    if (pi < 0) return null
    const pillar = goalsData[pi]
    const summaryMap = meta?.weeklySummary || {}
    const goals = []
    if (Array.isArray(pillar.goals)) {
      for (let gi = 0; gi < pillar.goals.length; gi++) {
        const g = pillar.goals[gi]
        if (!g || g.type === 'section') continue
        const summary = summaryMap[`p${pi}-g${gi}`] || {}
        const rawSubs = g.subgoals || g.subGoals || []
        const subs = (Array.isArray(rawSubs) ? rawSubs : [])
          .map((s) => ({
            name: s?.subgoal || '',
            owner: s?.owner || '',
            current: s?.current,
            target: s?.target,
            unit: s?.unit || '',
            update: s?.update || '',
          }))
          .filter(
            (s) =>
              s.name ||
              s.update ||
              (s.current !== undefined && s.current !== null && s.current !== '')
          )
        goals.push({
          goalName: g.goal || 'Untitled goal',
          dri: g.dri || '',
          current: g.current,
          target: g.target,
          unit: g.unit || '',
          tldr: summary.tldr || '',
          wins: summary.wins || '',
          flags: summary.flags || '',
          subgoals: subs,
        })
      }
    }
    return {
      pillarName: pillar.pillar,
      description: pillar.description || '',
      goals,
    }
  }, [goalsData, meta?.weeklySummary])

  // Orphan goal candidates — goals with weekly summaries written this
  // week whose DRI doesn't match any team's DRI (so they don't flow
  // through the team-summary path into the Company Update). Zoe picks
  // which ones, if any, to include via checkboxes in the Company
  // Update section.
  const orphanCandidates = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const summaryMap = meta?.weeklySummary || {}
    const teamDris = new Set(TEAMS.map((t) => {
      const entry = meta?.teamUpdates?.[t.id]
      return (entry?.dri || t.defaultDri || '').trim()
    }).filter(Boolean))
    const out = []
    for (let pi = 0; pi < goalsData.length; pi++) {
      const pillar = goalsData[pi]
      if (!pillar?.goals) continue
      for (let gi = 0; gi < pillar.goals.length; gi++) {
        const g = pillar.goals[gi]
        if (!g || g.type === 'section') continue
        if (!g.dri || teamDris.has(g.dri)) continue
        const key = `p${pi}-g${gi}`
        const summary = summaryMap[key] || {}
        const hasContent = !!(summary.tldr || summary.wins || summary.flags)
        if (!hasContent) continue
        out.push({
          key,
          goalName: g.goal || 'Untitled goal',
          pillar: pillar.pillar,
          dri: g.dri,
          tldr: summary.tldr || '',
          wins: summary.wins || '',
          flags: summary.flags || '',
          savedBy: summary.savedBy || '',
        })
      }
    }
    return out
  }, [goalsData, meta?.weeklySummary, meta?.teamUpdates])

  const toggleOrphanKey = (key) => {
    setCompanyOrphanKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleGenerateCompany = async () => {
    if (!canAuthorCompanyUpdate) return
    setCompanyError(null)
    setCompanyGenerating(true)
    try {
      // Translate the checked orphan keys into the payload shape the
      // worker expects. Skip silently if a checked key no longer
      // appears in candidates (e.g., the goal was deleted between
      // generations) — that's harmless.
      const selectedOrphans = orphanCandidates.filter((c) =>
        companyOrphanKeys.has(c.key)
      )
      // NOTE: we deliberately do NOT pass weeklySummariesForAi here.
      // For the Company Update, team summaries are the canonical body
      // content for each Updates by Area subsection; sending the
      // underlying goal summaries on top would invite the AI to repeat
      // the same event (once in the team subsection, again as a goal
      // callout). Orphan goals — explicitly selected by Zoe — are the
      // ONLY individual-goal content. Sub-goal updates carry through
      // for depth/specifics that team summaries may not capture.
      const result = await generateCompany.mutateAsync({
        subgoalUpdates: subgoalUpdatesForAi,
        teamUpdates: teamUpdatesForAi,
        bigPicture,
        orphanGoals: selectedOrphans,
        // EO Buyouts pillar content powers the Upcoming Transactions
        // section. Worker prompt scopes it to that section only so it
        // doesn't double up with the Delivery team summary elsewhere.
        eoBuyoutsContent,
      })
      // Fresh draft replaces any in-flight edit
      setCompanyPreview({
        markdown: result?.markdown || '',
        generatedAt: result?.generatedAt || new Date().toISOString(),
      })
      setCompanyDraft(null)
    } catch (err) {
      console.error('[company-update] generate failed', err)
      setCompanyError(
        err?.message ||
          "Couldn't generate the Company Update — check the worker logs."
      )
    } finally {
      setCompanyGenerating(false)
    }
  }

  const handleSaveCompany = () => {
    if (!canAuthorCompanyUpdate) return
    const markdown =
      companyDraft !== null
        ? companyDraft
        : companyPreview?.markdown ?? company?.markdown ?? ''
    if (!markdown.trim()) return
    const isFromPreview = companyPreview !== null && companyDraft === null
    saveCompany.mutate({
      markdown,
      generatedAt: isFromPreview
        ? companyPreview.generatedAt
        : company?.generatedAt || new Date().toISOString(),
      generatedBy: company?.generatedBy || currentUser,
      edited: !isFromPreview,
    })
    setCompanyDraft(null)
    setCompanyPreview(null)
    // Selections were one-shot for this drafting session — clear so
    // next week starts with a fresh picker.
    setCompanyOrphanKeys(new Set())
  }

  const handleCancelCompany = () => {
    setCompanyDraft(null)
    setCompanyPreview(null)
    setCompanyError(null)
    setCompanyOrphanKeys(new Set())
  }

  const handleClearCompany = () => {
    if (!canAuthorCompanyUpdate) return
    if (!confirm('Clear the current Company Update?')) return
    saveCompany.mutate({ clear: true })
    setCompanyDraft(null)
    setCompanyPreview(null)
  }

  const handleCopyCompany = async () => {
    const markdown =
      companyDraft !== null
        ? companyDraft
        : companyPreview?.markdown ?? company?.markdown ?? ''
    if (!markdown) return
    try {
      await navigator.clipboard.writeText(markdown)
      setCompanyCopied(true)
      setTimeout(() => setCompanyCopied(false), 1500)
    } catch (err) {
      console.warn('[company-update] copy failed', err)
    }
  }

  // ── Quarterly Pre-read ─────────────────────────────────────────────
  const preread = meta?.quarterlyPreread || null

  // Pillar health rollup — count goals per status per pillar, compute
  // worst-case status. Sent to the worker so the AI's goal health
  // section is grounded in DRI-set classifications.
  const pillarHealth = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    return goalsData
      .filter((p) => p?.pillar && p.pillar !== 'Team Updates')
      .map((p) => {
        const counts = {
          'on-track': 0,
          'at-risk': 0,
          'off-track': 0,
          done: 0,
          'not-started': 0,
        }
        for (const g of p.goals || []) {
          if (!g || g.type === 'section') continue
          const status = resolveStatus(g.status).value
          if (counts[status] !== undefined) counts[status] += 1
        }
        // Worst-case rollup — one off-track wins, then at-risk, etc.
        let rollup = 'on-track'
        if (counts['off-track'] > 0) rollup = 'off-track'
        else if (counts['at-risk'] > 0) rollup = 'at-risk'
        else if (counts['on-track'] === 0 && counts['not-started'] > 0) rollup = 'not-started'
        else if (counts['on-track'] === 0 && counts.done > 0) rollup = 'done'
        return {
          pillarName: p.pillar,
          status: rollup,
          goalCounts: {
            onTrack: counts['on-track'],
            atRisk: counts['at-risk'],
            offTrack: counts['off-track'],
            done: counts.done,
            notStarted: counts['not-started'],
          },
        }
      })
  }, [goalsData])

  const handleGeneratePreread = async () => {
    if (!canAuthorCompanyUpdate) return
    setPrereadError(null)
    setPrereadGenerating(true)
    try {
      const result = await generatePreread.mutateAsync({
        weeklySummaries: weeklySummariesForAi,
        teamUpdates: teamUpdatesForAi,
        pillarHealth,
        bigPicture,
      })
      setPrereadPreview({
        topLine: result?.topLine || '',
        goalHealth: result?.goalHealth || [],
        highlights: result?.highlights || [],
        needsAttention: result?.needsAttention || [],
        generatedAt: result?.generatedAt || new Date().toISOString(),
      })
    } catch (err) {
      console.error('[preread] generate failed', err)
      setPrereadError(
        err?.message ||
          "Couldn't generate the Pre-read — check the worker logs."
      )
    } finally {
      setPrereadGenerating(false)
    }
  }

  const handleSavePreread = () => {
    if (!canAuthorCompanyUpdate) return
    const source = prereadPreview || preread
    if (!source) return
    const isFromPreview = !!prereadPreview
    savePreread.mutate({
      topLine: source.topLine || '',
      goalHealth: source.goalHealth || [],
      highlights: source.highlights || [],
      needsAttention: source.needsAttention || [],
      generatedAt: isFromPreview
        ? prereadPreview.generatedAt
        : preread?.generatedAt || new Date().toISOString(),
      generatedBy: preread?.generatedBy || currentUser,
      edited: !isFromPreview,
    })
    setPrereadPreview(null)
  }

  const handleCancelPreread = () => {
    setPrereadPreview(null)
    setPrereadError(null)
  }

  const handleClearPreread = () => {
    if (!canAuthorCompanyUpdate) return
    if (!confirm('Clear the current Quarterly Pre-read?')) return
    savePreread.mutate({ clear: true })
    setPrereadPreview(null)
  }

  const handleCopyPreread = async () => {
    const source = prereadPreview || preread
    if (!source) return
    const markdown = prereadToMarkdown(source, bigPicture?.weekDateLabel)
    if (!markdown) return
    try {
      await navigator.clipboard.writeText(markdown)
      setPrereadCopied(true)
      setTimeout(() => setPrereadCopied(false), 1500)
    } catch (err) {
      console.warn('[preread] copy failed', err)
    }
  }

  // Per-section edit starters — branch on whether we have bullets or legacy prose.
  // In archive mode, these are no-ops so the Edit buttons never open a draft.
  const startEditTldr = () => {
    if (readOnly) return
    setDraftTldr(view?.tldr || '')
  }
  const startEditWins = () => {
    if (readOnly) return
    if (winBullets) {
      setDraftWinBullets([...winBullets])
    } else {
      setDraftWinsProse(winsProse)
    }
  }
  const startEditFlags = () => {
    if (readOnly) return
    if (flagBullets) {
      setDraftFlagBullets([...flagBullets])
    } else {
      setDraftFlagsProse(flagsProse)
    }
  }

  const handleAddPref = () => {
    const text = newPrefText.trim()
    if (!text) return
    const pref = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 5),
      text,
      addedAt: new Date().toISOString(),
      addedBy: currentUser,
    }
    prefOp.mutate({
      op: 'create',
      pref,
      optimistic: (list) => [...list, pref],
    })
    setNewPrefText('')
  }
  const handleRemovePref = (id) => {
    prefOp.mutate({
      op: 'remove',
      id,
      optimistic: (list) => list.filter((p) => p.id !== id),
    })
  }

  const showContent = !!view
  const generating = generateExec.isPending

  return (
    <section
      data-tour="exec-summary"
      style={{
        margin: '0 0 16px',
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <header
        onClick={() => setExpanded((e) => !e)}
        style={{
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)',
          borderBottom: expanded ? '1px solid var(--border)' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 18 }}>✨</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>
            Executive Summary
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            {hasPendingPreview ? (
              <>Just generated — not saved yet</>
            ) : exec?.generatedAt ? (
              <>
                {exec.edited ? 'Edited' : 'Generated'}{' '}
                {timeAgo(new Date(exec.generatedAt).getTime())}
                {exec.generatedBy ? ` by ${exec.generatedBy}` : ''}
              </>
            ) : (
              "No summary yet — generate one from this week's updates"
            )}
          </div>
        </div>
        {archiveCount > 0 && (
          <button
            data-tour="archive-button"
            onClick={(e) => {
              e.stopPropagation()
              openArchive('latest')
            }}
            title="View past weekly snapshots"
            style={{
              padding: '4px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--muted)',
              background: '#fff',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            📅 Past weeks ({archiveCount})
          </button>
        )}
        <span
          style={{
            fontSize: 12,
            color: 'var(--muted)',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.15s',
          }}
        >
          ▾
        </span>
      </header>

      {expanded && (
        <>
          {/* Staleness bar — skip entirely in archive mode; "new updates since generated"
              isn't meaningful when you're looking at a frozen past week. */}
          {!readOnly && (
            <StalenessBar onRegenerate={handleGenerate} regenerating={generating} />
          )}

          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 18 }}>
            {!showContent ? (
              <EmptyState
                onGenerate={handleGenerate}
                generating={generating}
                goalsWithUpdates={goalsWithUpdates}
                totalGoals={totalGoals}
              />
            ) : (
              <>
                {/* TLDR */}
                <SectionHeader
                  label="TLDR"
                  color="var(--muted)"
                  editing={isEditingTldr}
                  onStartEdit={startEditTldr}
                  readOnly={readOnly}
                />
                {isEditingTldr ? (
                  <AutoSizingTextarea
                    autoFocus
                    value={tldrValue}
                    onChange={(e) => setDraftTldr(e.target.value)}
                    placeholder="A 2-4 sentence summary of where Q2 stands overall."
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setDraftTldr(null)
                    }}
                    style={{ minHeight: 72 }}
                  />
                ) : tldrValue ? (
                  <ReadOnlyProse
                    text={tldrValue}
                    onClick={readOnly ? undefined : startEditTldr}
                  />
                ) : readOnly ? null : (
                  <ClickToAdd onClick={startEditTldr}>
                    No TLDR yet — click to add one.
                  </ClickToAdd>
                )}

                {/* Wins */}
                <SectionHeader
                  label="Wins"
                  color="#166534"
                  editing={isEditingWins}
                  onStartEdit={startEditWins}
                  readOnly={readOnly}
                />
                {isEditingWins ? (
                  draftWinBullets !== null ? (
                    <SummaryBulletList
                      bullets={draftWinBullets}
                      accent="#0F6E56"
                      editing
                      onChange={setDraftWinBullets}
                    />
                  ) : (
                    <AutoSizingTextarea
                      autoFocus
                      value={draftWinsProse ?? ''}
                      onChange={(e) => setDraftWinsProse(e.target.value)}
                      placeholder="What went well this week?"
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setDraftWinsProse(null)
                      }}
                    />
                  )
                ) : winBullets && winBullets.length > 0 ? (
                  <SummaryBulletList
                    bullets={winBullets}
                    accent="#0F6E56"
                    emptyLabel="No wins called out this week."
                  />
                ) : winsProse ? (
                  <ReadOnlyProse
                    text={winsProse}
                    onClick={readOnly ? undefined : startEditWins}
                  />
                ) : readOnly ? null : (
                  <ClickToAdd onClick={startEditWins}>
                    No wins called out yet.
                  </ClickToAdd>
                )}

                {/* Flags */}
                <SectionHeader
                  label="Flags & Risks"
                  color="#b91c1c"
                  editing={isEditingFlags}
                  onStartEdit={startEditFlags}
                  readOnly={readOnly}
                />
                {isEditingFlags ? (
                  draftFlagBullets !== null ? (
                    <SummaryBulletList
                      bullets={draftFlagBullets}
                      accent="#A32D2D"
                      editing
                      onChange={setDraftFlagBullets}
                    />
                  ) : (
                    <AutoSizingTextarea
                      autoFocus
                      value={draftFlagsProse ?? ''}
                      onChange={(e) => setDraftFlagsProse(e.target.value)}
                      placeholder="What's at risk, blocked, or needs leadership attention?"
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setDraftFlagsProse(null)
                      }}
                    />
                  )
                ) : flagBullets && flagBullets.length > 0 ? (
                  <SummaryBulletList
                    bullets={flagBullets}
                    accent="#A32D2D"
                    emptyLabel="No flags called out this week."
                  />
                ) : flagsProse ? (
                  <ReadOnlyProse
                    text={flagsProse}
                    onClick={readOnly ? undefined : startEditFlags}
                  />
                ) : readOnly ? null : (
                  <ClickToAdd onClick={startEditFlags}>
                    No flags called out yet.
                  </ClickToAdd>
                )}

                {/* Legend */}
                <Legend />
              </>
            )}

            {/* Action buttons — hidden in archive mode */}
            {showContent && !readOnly && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  borderTop: '1px solid var(--border)',
                  paddingTop: 12,
                }}
              >
                {dirty ? (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={saveExec.isPending}
                      style={primaryBtn}
                    >
                      {saveExec.isPending
                        ? 'Saving…'
                        : hasPendingPreview
                        ? 'Save as current'
                        : 'Save edits'}
                    </button>
                    <button onClick={handleCancel} style={secondaryBtn}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleGenerate}
                      disabled={generating || goalsWithUpdates === 0}
                      style={primaryBtn}
                      title={
                        goalsWithUpdates === 0
                          ? 'Add weekly TLDR/Wins/Flags to at least one goal first'
                          : 'Ask Claude for a fresh summary based on all goals'
                      }
                    >
                      {generating ? 'Generating…' : 'Regenerate'}
                    </button>
                    <button onClick={handleClear} style={dangerBtn}>
                      Clear
                    </button>
                  </>
                )}
                <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted-light)' }}>
                  {totalGoals > 0
                    ? `${goalsWithUpdates}/${totalGoals} goal${totalGoals === 1 ? '' : 's'} have weekly updates${
                        subgoalUpdatesForAi.length > 0
                          ? ` · ${subgoalUpdatesForAi.length} sub-goal note${subgoalUpdatesForAi.length === 1 ? '' : 's'}`
                          : ''
                      }`
                    : 'No goals yet'}
                </span>
              </div>
            )}

            {/* Quarterly Pre-read — structured Monday status snapshot
                for Zoe's email + leadership Tuesday meeting. Distinct
                from the Company Update below. */}
            <QuarterlyPrereadSection
              preread={preread}
              previewData={prereadPreview}
              generating={prereadGenerating}
              saving={savePreread.isPending}
              error={prereadError}
              copied={prereadCopied}
              canAuthor={canAuthorCompanyUpdate && !readOnly}
              onGenerate={handleGeneratePreread}
              onSave={handleSavePreread}
              onCancel={handleCancelPreread}
              onClear={handleClearPreread}
              onCopy={handleCopyPreread}
            />

            {/* Company Update — long-form Monday update for Zoe.
                Read-only for everyone else; the buttons only render
                when canAuthorCompanyUpdate is true. */}
            <CompanyUpdateSection
              company={company}
              preview={companyPreview}
              draft={companyDraft}
              setDraft={setCompanyDraft}
              generating={companyGenerating}
              saving={saveCompany.isPending}
              error={companyError}
              copied={companyCopied}
              canAuthor={canAuthorCompanyUpdate && !readOnly}
              orphanCandidates={orphanCandidates}
              orphanKeys={companyOrphanKeys}
              onToggleOrphan={toggleOrphanKey}
              onGenerate={handleGenerateCompany}
              onSave={handleSaveCompany}
              onCancel={handleCancelCompany}
              onClear={handleClearCompany}
              onCopy={handleCopyCompany}
            />

            {/* Follow-ups */}
            <div
              data-tour="follow-ups"
              style={{
                borderTop: '1px solid var(--border)',
                paddingTop: 12,
              }}
            >
              <FollowUpsSection />
            </div>

            {/* AI preferences */}
            <div style={{ marginTop: 4 }}>
              <button
                onClick={() => setEditingPrefs((e) => !e)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--muted)',
                  cursor: 'pointer',
                  padding: 0,
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                }}
              >
                {editingPrefs ? '▾' : '▸'} Writing preferences ({prefs.length})
              </button>
              {editingPrefs && (
                <PreferencesEditor
                  prefs={prefs}
                  newText={newPrefText}
                  setNewText={setNewPrefText}
                  onAdd={handleAddPref}
                  onRemove={handleRemovePref}
                  saving={prefOp.isPending}
                />
              )}
            </div>
          </div>
        </>
      )}
    </section>
  )
}

/* ─── Sub-components ──────────────────────────────────────────────── */

/**
 * QuarterlyPrereadSection — structured Monday pre-read for Zoe's email
 * and the leadership team's Tuesday meeting prep. Distinct from the
 * long-form Company Update; co-exists in the same card.
 *
 * Modes:
 *   - empty / no current preread: Generate button (canAuthor only)
 *   - preview (post-generate, pre-save): rendered + Save / Regenerate /
 *     Copy / Cancel
 *   - saved: rendered + Edit-via-Regenerate / Copy / Clear (canAuthor)
 *
 * Non-author viewers see the saved version read-only with a Copy button.
 * Section auto-hides for non-authors when nothing is saved.
 */
function QuarterlyPrereadSection({
  preread,
  previewData,
  generating,
  saving,
  error,
  copied,
  canAuthor,
  onGenerate,
  onSave,
  onCancel,
  onClear,
  onCopy,
}) {
  const hasSaved = !!preread
  const hasPreview = !!previewData

  if (!hasSaved && !hasPreview && !canAuthor) return null

  const visible = previewData || preread

  return (
    <div
      style={{
        borderTop: '1px solid var(--border)',
        paddingTop: 14,
        marginTop: 4,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 8,
        }}
      >
        <div>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
              letterSpacing: 0.02,
            }}
          >
            Quarterly Pre-read
          </p>
          <p
            style={{
              fontSize: 11,
              color: 'var(--muted)',
              margin: '2px 0 0',
              fontStyle: 'italic',
            }}
          >
            {hasPreview
              ? 'Preview — review and save to publish'
              : hasSaved
                ? `Published ${preread.generatedBy ? `by ${preread.generatedBy} ` : ''}· ${preread.generatedAt ? timeAgoSafe(preread.generatedAt) : 'recently'}${preread.edited ? ' · edited' : ''}`
                : "Email-ready summary of weekly progress against quarterly goals — pulls top-line metrics, pillar health, cross-team highlights, and what needs attention."}
          </p>
        </div>
      </div>

      {error && (
        <div
          style={{
            fontSize: 12,
            color: '#991b1b',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            padding: '6px 10px',
            marginBottom: 8,
          }}
        >
          {error}
        </div>
      )}

      {!hasSaved && !hasPreview && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--muted)',
            background: '#fafafa',
            border: '1px dashed var(--border)',
            borderRadius: 8,
            padding: '14px 16px',
            textAlign: 'center',
          }}
        >
          No Pre-read generated yet this week.
          {canAuthor ? ' Click Generate to draft one.' : ''}
        </div>
      )}

      {visible && (
        <div
          style={{
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '16px 18px',
            maxHeight: 520,
            overflowY: 'auto',
          }}
        >
          <QuarterlyPrereadView preread={visible} />
        </div>
      )}

      {(canAuthor || hasSaved) && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginTop: 10,
            alignItems: 'center',
          }}
        >
          {canAuthor && hasPreview && (
            <>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                style={primaryActionStyle()}
              >
                {saving ? 'Saving…' : 'Save as current'}
              </button>
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating || saving}
                style={secondaryActionStyle()}
              >
                {generating ? 'Regenerating…' : 'Regenerate'}
              </button>
              <button
                type="button"
                onClick={onCopy}
                style={secondaryActionStyle()}
              >
                {copied ? 'Copied!' : 'Copy as markdown'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                style={tertiaryActionStyle()}
              >
                Cancel
              </button>
            </>
          )}
          {canAuthor && !hasPreview && !hasSaved && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating}
              style={primaryActionStyle()}
              title="Pulls quarterly goal health, weekly summaries, and team updates into a structured email-ready pre-read"
            >
              {generating ? 'Generating…' : 'Generate Quarterly Pre-read'}
            </button>
          )}
          {canAuthor && !hasPreview && hasSaved && (
            <>
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating}
                style={secondaryActionStyle()}
              >
                {generating ? 'Regenerating…' : 'Regenerate'}
              </button>
              <button type="button" onClick={onCopy} style={secondaryActionStyle()}>
                {copied ? 'Copied!' : 'Copy as markdown'}
              </button>
              <button type="button" onClick={onClear} style={dangerActionStyle()}>
                Clear
              </button>
            </>
          )}
          {!canAuthor && hasSaved && (
            <button type="button" onClick={onCopy} style={secondaryActionStyle()}>
              {copied ? 'Copied!' : 'Copy as markdown'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * CompanyUpdateSection — long-form Monday update Zoe ships every week.
 *
 * Three rendering modes:
 *   - preview pane (after Generate): shows the markdown draft + Save /
 *     Regenerate / Copy / Cancel
 *   - editing draft of an existing saved update: textarea + Save /
 *     Cancel
 *   - read-only display of the current saved update: rendered markdown
 *     + Edit / Regenerate / Copy / Clear (last 4 only for canAuthor)
 *
 * For non-author viewers (everyone except Zoe + Ricky), this section
 * either renders the saved update read-only or hides itself entirely
 * if nothing is published yet — so it doesn't clutter the dashboard
 * with an empty "no update yet" placeholder.
 */
function CompanyUpdateSection({
  company,
  preview,
  draft,
  setDraft,
  generating,
  saving,
  error,
  copied,
  canAuthor,
  orphanCandidates,
  orphanKeys,
  onToggleOrphan,
  onGenerate,
  onSave,
  onCancel,
  onClear,
  onCopy,
}) {
  // Hide entirely when there's nothing to show and the viewer can't
  // author. Keeps the exec summary card clean for the rest of the team
  // until Zoe publishes the first update of the week.
  const hasPreview = !!preview
  const hasDraft = draft !== null
  const hasSaved = !!company?.markdown
  if (!hasSaved && !hasPreview && !hasDraft && !canAuthor) return null

  const headerLabel = 'Company Weekly Update'
  const dirty = hasPreview || hasDraft

  // Decide which markdown to show: draft (editing) > preview (just
  // generated) > saved.
  const visibleMarkdown = hasDraft
    ? draft
    : preview?.markdown ?? company?.markdown ?? ''

  // For "click to edit" on a saved update (only for authors)
  const startEdit = () => {
    if (!canAuthor) return
    setDraft(company?.markdown || '')
  }

  return (
    <div
      style={{
        borderTop: '1px solid var(--border)',
        paddingTop: 14,
        marginTop: 4,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 8,
        }}
      >
        <div>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
              letterSpacing: 0.02,
            }}
          >
            {headerLabel}
          </p>
          <p
            style={{
              fontSize: 11,
              color: 'var(--muted)',
              margin: '2px 0 0',
              fontStyle: 'italic',
            }}
          >
            {hasPreview
              ? 'Preview — review and save to publish to the team'
              : hasDraft
                ? 'Editing — click Save to publish your changes'
                : hasSaved
                  ? `Published ${company.generatedBy ? `by ${company.generatedBy} ` : ''}· ${company.generatedAt ? timeAgoSafe(company.generatedAt) : 'recently'}${company.edited ? ' · edited' : ''}`
                  : "Long-form Monday update — pulls team updates, goal summaries, and the Big Picture into a draft in Zoe's voice"}
          </p>
        </div>
      </div>

      {/* Orphan goal picker — visible when the lead is authoring AND
          there are orphan candidates (goals owned by people outside
          the team DRIs that have weekly summaries this week). The
          checked set is sent with the next Generate / Regenerate
          call, and reset after Save / Cancel. */}
      {canAuthor &&
        !(hasSaved && !hasPreview && !hasDraft) &&
        orphanCandidates &&
        orphanCandidates.length > 0 && (
          <OrphanGoalPicker
            candidates={orphanCandidates}
            checkedKeys={orphanKeys}
            onToggle={onToggleOrphan}
          />
        )}

      {error && (
        <div
          style={{
            fontSize: 12,
            color: '#991b1b',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            padding: '6px 10px',
            marginBottom: 8,
          }}
        >
          {error}
        </div>
      )}

      {/* Empty state — no saved update, nothing being edited */}
      {!hasSaved && !hasPreview && !hasDraft && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--muted)',
            background: '#fafafa',
            border: '1px dashed var(--border)',
            borderRadius: 8,
            padding: '14px 16px',
            textAlign: 'center',
          }}
        >
          No Company Update published yet this week.
          {canAuthor ? ' Click Generate to draft one.' : ''}
        </div>
      )}

      {/* Editing the saved markdown directly */}
      {hasDraft && (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={16}
          spellCheck
          style={{
            width: '100%',
            fontSize: 13,
            fontFamily: 'ui-monospace, monospace',
            color: 'var(--text)',
            border: '1px solid var(--accent)',
            borderRadius: 8,
            padding: '10px 12px',
            background: '#fff',
            resize: 'vertical',
            lineHeight: 1.5,
            outline: 'none',
            minHeight: 280,
            boxSizing: 'border-box',
          }}
        />
      )}

      {/* Preview rendering — newly generated draft, or saved update */}
      {!hasDraft && (hasPreview || hasSaved) && (
        <div
          style={{
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '18px 22px',
            maxHeight: 520,
            overflowY: 'auto',
          }}
        >
          <CompanyUpdateView markdown={visibleMarkdown} />
        </div>
      )}

      {/* Action row */}
      {(canAuthor || hasSaved) && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginTop: 10,
            alignItems: 'center',
          }}
        >
          {canAuthor && dirty && (
            <>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                style={primaryActionStyle()}
              >
                {saving
                  ? 'Saving…'
                  : hasPreview
                    ? 'Save as current'
                    : 'Save edits'}
              </button>
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating || saving}
                style={secondaryActionStyle()}
              >
                {generating ? 'Regenerating…' : 'Regenerate'}
              </button>
              <button
                type="button"
                onClick={onCopy}
                disabled={!visibleMarkdown}
                style={secondaryActionStyle()}
              >
                {copied ? 'Copied!' : 'Copy as markdown'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                style={tertiaryActionStyle()}
              >
                Cancel
              </button>
            </>
          )}
          {canAuthor && !dirty && !hasSaved && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating}
              style={primaryActionStyle()}
              title="Pulls team updates, goal summaries, and the Big Picture into a markdown draft in Zoe's voice"
            >
              {generating ? 'Generating…' : 'Generate Company Update'}
            </button>
          )}
          {canAuthor && !dirty && hasSaved && (
            <>
              <button type="button" onClick={startEdit} style={secondaryActionStyle()}>
                Edit
              </button>
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating}
                style={secondaryActionStyle()}
              >
                {generating ? 'Regenerating…' : 'Regenerate'}
              </button>
              <button type="button" onClick={onCopy} style={secondaryActionStyle()}>
                {copied ? 'Copied!' : 'Copy as markdown'}
              </button>
              <button type="button" onClick={onClear} style={dangerActionStyle()}>
                Clear
              </button>
            </>
          )}
          {!canAuthor && hasSaved && (
            <button type="button" onClick={onCopy} style={secondaryActionStyle()}>
              {copied ? 'Copied!' : 'Copy as markdown'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * OrphanGoalPicker — checkbox list of goals whose DRI doesn't match
 * any team DRI. These don't flow through team summaries automatically,
 * so Zoe ticks the ones she wants pulled into the Company Update.
 *
 * Collapsible (closed by default to keep the section compact); the
 * count badge in the header reflects how many are checked, so even
 * collapsed it tells you how many orphans are coming through.
 */
function OrphanGoalPicker({ candidates, checkedKeys, onToggle }) {
  const [open, setOpen] = useState(false)
  const checkedCount =
    candidates.filter((c) => checkedKeys?.has(c.key)).length || 0

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        background: '#fafafa',
        borderRadius: 8,
        marginBottom: 10,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          padding: '8px 12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: 'var(--muted-light)',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.1s',
            display: 'inline-block',
            width: 10,
          }}
        >
          ▼
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>
          Other goal updates this week
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          ({candidates.length} not flowing through any team)
        </span>
        {checkedCount > 0 && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 10,
              fontWeight: 700,
              color: '#fff',
              background: 'var(--accent)',
              padding: '2px 8px',
              borderRadius: 99,
            }}
          >
            {checkedCount} selected
          </span>
        )}
      </button>

      {open && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <p
            style={{
              fontSize: 11,
              color: 'var(--muted)',
              margin: 0,
              padding: '8px 12px 4px',
              fontStyle: 'italic',
            }}
          >
            Tick any to include in the next Generate / Regenerate. Selections
            reset after Save or Cancel.
          </p>
          {candidates.map((c) => {
            const checked = !!checkedKeys?.has(c.key)
            const tldrSnippet =
              c.tldr && c.tldr.length > 140
                ? c.tldr.slice(0, 137).trim() + '…'
                : c.tldr || ''
            return (
              <label
                key={c.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '8px 12px',
                  borderTop: '1px solid #f1f5f9',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(c.key)}
                  style={{ marginTop: 2, cursor: 'pointer' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text)',
                    }}
                  >
                    {c.goalName}
                    {c.dri && (
                      <span style={{ color: 'var(--muted)', fontWeight: 500 }}>
                        {' '}— {c.dri}
                      </span>
                    )}
                    {c.pillar && (
                      <span style={{ color: 'var(--muted-light)', fontWeight: 500 }}>
                        {' '}· {c.pillar}
                      </span>
                    )}
                  </div>
                  {tldrSnippet && (
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--muted)',
                        marginTop: 2,
                        lineHeight: 1.45,
                      }}
                    >
                      {tldrSnippet}
                    </div>
                  )}
                </div>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

function timeAgoSafe(iso) {
  try {
    return timeAgo(new Date(iso).getTime())
  } catch {
    return ''
  }
}

function primaryActionStyle() {
  return {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 6,
    cursor: 'pointer',
  }
}

function secondaryActionStyle() {
  return {
    background: '#fff',
    color: 'var(--text)',
    border: '1px solid var(--border)',
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    cursor: 'pointer',
  }
}

function tertiaryActionStyle() {
  return {
    background: 'transparent',
    color: 'var(--muted)',
    border: '1px solid var(--border)',
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    cursor: 'pointer',
  }
}

function dangerActionStyle() {
  return {
    background: '#fff',
    color: '#dc2626',
    border: '1px solid #fecaca',
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    cursor: 'pointer',
  }
}

function SectionHeader({ label, color, editing, onStartEdit, readOnly }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: -6, // tighten spacing against the content below
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          color,
          textTransform: 'uppercase',
          letterSpacing: 0.08,
        }}
      >
        {label}
      </div>
      {!editing && !readOnly && (
        <button
          type="button"
          onClick={onStartEdit}
          title={`Edit ${label}`}
          style={{
            background: 'transparent',
            border: '1px solid var(--border)',
            padding: '2px 10px',
            borderRadius: 6,
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--muted)',
            cursor: 'pointer',
          }}
        >
          Edit
        </button>
      )}
    </div>
  )
}

function ReadOnlyProse({ text, onClick }) {
  return (
    <div
      onClick={onClick}
      title="Click to edit"
      style={{
        fontSize: 13,
        color: 'var(--text)',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        cursor: 'text',
        padding: '2px 0',
      }}
    >
      <RichText text={text} />
    </div>
  )
}

function ClickToAdd({ onClick, children }) {
  return (
    <div
      onClick={onClick}
      style={{
        fontSize: 12,
        color: 'var(--muted-light)',
        fontStyle: 'italic',
        cursor: 'text',
        padding: '2px 0',
      }}
    >
      {children}
    </div>
  )
}

function Legend() {
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: 0.08,
          color: 'var(--muted-light)',
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        What the colors mean
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 11, color: 'var(--muted)' }}>
        <LegendRow color="#185FA5" bg="#E6F1FB" label="Goal" hint="From a goal's weekly update" />
        <LegendRow color="#0F6E56" bg="#E1F5EE" label="Team" hint="From a team DRI's update" />
        <LegendRow color="#5F5E5A" bg="#F1EFE8" label="Other" hint="Pattern the AI noticed" />
      </div>
    </div>
  )
}

function LegendRow({ color, bg, label, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color,
          background: bg,
          padding: '1px 7px',
          borderRadius: 99,
        }}
      >
        {label}
      </span>
      <span>{hint}</span>
    </div>
  )
}

function EmptyState({ onGenerate, generating, goalsWithUpdates, totalGoals }) {
  return (
    <div
      style={{
        padding: '18px 16px',
        background: '#fafafa',
        border: '1px dashed var(--border)',
        borderRadius: 8,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
        {goalsWithUpdates === 0 ? (
          <>
            No weekly updates yet — fill in TLDR / Wins / Flags on at least one
            goal, then generate a summary.
          </>
        ) : (
          <>
            Generate a consolidated TLDR, Wins, and Flags across all {totalGoals} Q2 goals.
          </>
        )}
      </div>
      <button
        onClick={onGenerate}
        disabled={generating || goalsWithUpdates === 0}
        style={{
          ...primaryBtn,
          opacity: goalsWithUpdates === 0 ? 0.5 : 1,
        }}
      >
        {generating ? 'Generating…' : '✨ Generate with AI'}
      </button>
    </div>
  )
}

function PreferencesEditor({ prefs, newText, setNewText, onAdd, onRemove, saving }) {
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
        Each preference is included with every AI summary request. Good examples:{' '}
        <em>"Plain language, no buzzwords"</em>,{' '}
        <em>"Call out Trust Enforcer progress explicitly"</em>,{' '}
        <em>"Keep TLDR under 3 sentences"</em>.
      </div>
      {prefs.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {prefs.map((p) => (
            <li
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                background: '#fafafa',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              <span style={{ flex: 1, color: 'var(--text-secondary)' }}>{p.text}</span>
              <button
                onClick={() => onRemove(p.id)}
                title="Remove"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--muted-light)',
                  fontSize: 14,
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAdd()
            }
          }}
          placeholder="Add a writing-style preference…"
          style={{
            flex: 1,
            padding: '6px 10px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 12,
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
        <button
          onClick={onAdd}
          disabled={!newText.trim() || saving}
          style={{
            ...primaryBtn,
            padding: '6px 12px',
            fontSize: 11,
          }}
        >
          Add
        </button>
      </div>
    </div>
  )
}

/* ─── Shared button styles ────────────────────────────────────────── */

const primaryBtn = {
  padding: '6px 14px',
  border: 'none',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
  background: 'var(--accent)',
  color: '#fff',
  cursor: 'pointer',
}

const secondaryBtn = {
  padding: '6px 14px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  background: '#fff',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
}

const dangerBtn = {
  padding: '6px 14px',
  border: '1px solid #fecaca',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  background: '#fff',
  color: '#b91c1c',
  cursor: 'pointer',
}
